
import { useState, useEffect } from 'react';

interface LoadingScreenProps {
  onLoadingComplete: () => void;
}

const LoadingScreen = ({ onLoadingComplete }: LoadingScreenProps) => {
  const [progress, setProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [eyePosition, setEyePosition] = useState({ x: 0, y: 0 });

  // Simulate loading progress
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        // Smooth easing progress
        const remaining = 100 - prev;
        const increment = Math.max(1, remaining * 0.08);
        return Math.min(100, prev + increment);
      });
    }, 50);

    return () => clearInterval(interval);
  }, []);

  // Trigger exit animation when loading completes
  useEffect(() => {
    if (progress >= 100) {
      setTimeout(() => {
        setIsExiting(true);
        setTimeout(onLoadingComplete, 800);
      }, 400);
    }
  }, [progress, onLoadingComplete]);

  // Animate eyes looking around
  useEffect(() => {
    const lookAround = () => {
      const angle = Math.random() * Math.PI * 2;
      const distance = 3 + Math.random() * 5;
      setEyePosition({
        x: Math.cos(angle) * distance,
        y: Math.sin(angle) * distance,
      });
    };

    const interval = setInterval(lookAround, 1500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className={`fixed inset-0 z-[9999] bg-black flex flex-col items-center justify-center transition-opacity duration-700 ${
        isExiting ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Floating particles background */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white/5"
            style={{
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `floatParticle ${8 + Math.random() * 8}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
      </div>

      {/* Main character container */}
      <div
        className={`relative transition-all duration-700 ${
          isExiting ? 'scale-150 opacity-0' : 'scale-100 opacity-100'
        }`}
        style={{
          animation: 'gentleFloat 4s ease-in-out infinite',
        }}
      >
        {/* Ambient glow */}
        <div
          className="absolute -inset-20 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%)',
            animation: 'pulseGlow 3s ease-in-out infinite',
          }}
        />

        {/* Character body */}
        <div className="relative" style={{ width: '120px', height: '160px' }}>
          {/* Body shadow */}
          <div
            className="absolute"
            style={{
              width: '100px',
              height: '130px',
              left: '15px',
              top: '20px',
              background: 'rgba(255,255,255,0.02)',
              borderRadius: '60% 40% 50% 50%',
              filter: 'blur(20px)',
            }}
          />

          {/* Main body */}
          <div
            className="absolute"
            style={{
              width: '100px',
              height: '130px',
              left: '10px',
              top: '10px',
              background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 50%, #000000 100%)',
              borderRadius: '60% 40% 50% 50%',
              boxShadow: `
                inset 5px 5px 20px rgba(255,255,255,0.03),
                inset -5px -5px 20px rgba(0,0,0,0.8),
                0 0 40px rgba(255,255,255,0.02)
              `,
              animation: 'bodyBreath 4s ease-in-out infinite',
            }}
          >
            {/* Highlight */}
            <div
              className="absolute"
              style={{
                width: '35px',
                height: '50px',
                left: '12px',
                top: '15px',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 100%)',
                borderRadius: '50%',
                filter: 'blur(5px)',
              }}
            />
          </div>

          {/* Left eye */}
          <div
            className="absolute"
            style={{
              width: '38px',
              height: '42px',
              left: '20px',
              top: '40px',
              background: 'radial-gradient(ellipse, #0a0a0a 0%, #050505 100%)',
              borderRadius: '50%',
              boxShadow: 'inset 0 0 12px rgba(0,0,0,0.9)',
            }}
          >
            <div
              className="absolute inset-1 rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(25,25,25,1) 0%, rgba(12,12,12,1) 100%)',
              }}
            >
              {/* Pupil */}
              <div
                className="absolute transition-all duration-500"
                style={{
                  width: '14px',
                  height: '14px',
                  left: '50%',
                  top: '50%',
                  transform: `translate(calc(-50% + ${eyePosition.x}px), calc(-50% + ${eyePosition.y}px))`,
                  background: 'radial-gradient(circle, #ffffff 0%, #e0e0e0 40%, #888 100%)',
                  borderRadius: '50%',
                  boxShadow: '0 0 10px rgba(255,255,255,0.3)',
                }}
              >
                <div
                  className="absolute"
                  style={{
                    width: '6px',
                    height: '6px',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: '#000',
                    borderRadius: '50%',
                  }}
                />
                <div
                  className="absolute"
                  style={{
                    width: '4px',
                    height: '4px',
                    left: '4px',
                    top: '2px',
                    background: 'rgba(255,255,255,0.9)',
                    borderRadius: '50%',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Right eye */}
          <div
            className="absolute"
            style={{
              width: '32px',
              height: '36px',
              left: '62px',
              top: '45px',
              background: 'radial-gradient(ellipse, #0a0a0a 0%, #050505 100%)',
              borderRadius: '50%',
              boxShadow: 'inset 0 0 10px rgba(0,0,0,0.9)',
            }}
          >
            <div
              className="absolute inset-1 rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(25,25,25,1) 0%, rgba(12,12,12,1) 100%)',
              }}
            >
              <div
                className="absolute transition-all duration-500"
                style={{
                  width: '12px',
                  height: '12px',
                  left: '50%',
                  top: '50%',
                  transform: `translate(calc(-50% + ${eyePosition.x * 0.8}px), calc(-50% + ${eyePosition.y * 0.8}px))`,
                  background: 'radial-gradient(circle, #ffffff 0%, #e0e0e0 40%, #888 100%)',
                  borderRadius: '50%',
                  boxShadow: '0 0 8px rgba(255,255,255,0.3)',
                }}
              >
                <div
                  className="absolute"
                  style={{
                    width: '5px',
                    height: '5px',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: '#000',
                    borderRadius: '50%',
                  }}
                />
                <div
                  className="absolute"
                  style={{
                    width: '3px',
                    height: '3px',
                    left: '3px',
                    top: '2px',
                    background: 'rgba(255,255,255,0.9)',
                    borderRadius: '50%',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Tentacles */}
          <div
            className="absolute"
            style={{
              width: '14px',
              height: '40px',
              right: '5px',
              top: '90px',
              background: 'linear-gradient(90deg, #1a1a1a 0%, #0a0a0a 100%)',
              borderRadius: '40% 60% 60% 40%',
              transformOrigin: 'left center',
              animation: 'tentacleWave 2.5s ease-in-out infinite',
            }}
          />
          <div
            className="absolute"
            style={{
              width: '11px',
              height: '28px',
              right: '12px',
              top: '115px',
              background: 'linear-gradient(90deg, #151515 0%, #080808 100%)',
              borderRadius: '40% 60% 60% 40%',
              transformOrigin: 'left center',
              animation: 'tentacleWave 3s ease-in-out infinite 0.5s',
            }}
          />
          <div
            className="absolute"
            style={{
              width: '9px',
              height: '20px',
              right: '22px',
              top: '130px',
              background: 'linear-gradient(90deg, #121212 0%, #060606 100%)',
              borderRadius: '40% 60% 60% 40%',
              transformOrigin: 'left center',
              animation: 'tentacleWave 3.5s ease-in-out infinite 1s',
            }}
          />
        </div>
      </div>

      {/* Loading bar */}
      <div className="mt-16 w-48">
        <div
          className="h-[2px] bg-white/10 rounded-full overflow-hidden"
          style={{
            boxShadow: '0 0 10px rgba(255,255,255,0.05)',
          }}
        >
          <div
            className="h-full bg-white/60 rounded-full transition-all duration-100 ease-out"
            style={{
              width: `${progress}%`,
              boxShadow: '0 0 10px rgba(255,255,255,0.3)',
            }}
          />
        </div>
        
        {/* Progress text */}
        <div className="mt-4 text-center">
          <span
            className="text-white/30 text-xs tracking-[0.3em] font-light"
            style={{ fontFamily: 'monospace' }}
          >
            {Math.round(progress)}%
          </span>
        </div>
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes gentleFloat {
          0%, 100% { transform: translateY(0) rotate(-1deg); }
          50% { transform: translateY(-12px) rotate(1deg); }
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }
        @keyframes bodyBreath {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        @keyframes tentacleWave {
          0%, 100% { transform: rotate(-5deg); }
          50% { transform: rotate(10deg); }
        }
        @keyframes floatParticle {
          0%, 100% { 
            transform: translateY(0) translateX(0);
            opacity: 0.3;
          }
          50% { 
            transform: translateY(-30px) translateX(10px);
            opacity: 0.6;
          }
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;
